import {
	Button,
	ConfirmPopover,
	Icon,
	LinkButton,
	MuiAlert,
	MuiMenuItem,
	ResponsiveDrawer,
	TextField,
	Typography,
	useDebounce
} from '@fox/atoms';
import { ResponsiveTable } from '@fox/data-grid';
import { useSubscribeTopicWithHandler } from '@fox/extensions';
import { FlexColumn, FlexColumnSpaced, FlexRow, FlexRowSpaced } from '@fox/layout';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useContentContext } from '../../content-context';
import { CustomFieldValueConverter } from '../membership-form-template/fields';
import { useCreateOrgMembership, useRemoveOrgMembership } from '../mutation-hooks/mutation-implementations';
import { useRepresentativeRelationsData } from '../query-hooks/query-implementations';
import { EntityInfo, Relation } from './contracts';
import { useRepresentativesContext } from './representatives-context';
import { representativeEntityId } from './util';

export const SearchTitleWrapper = styled.div`
	margin-bottom: 1rem;
	align-items: center;
	gap: 1rem;
	flex-direction: row;
	display: flex;
	@media (max-width: 800px) {
		flex-direction: column;
	}
`;

export function SourceOrganisation({ source }: { source: EntityInfo }) {
	const { unit } = useRepresentativesContext();
	const { contentId, requestToken } = useContentContext();
	const [page, setPage] = useState<number>(1);
	const PAGE_SIZE = 10;
	const [search, setSearch] = useState<string>('');
	const debouncedSearch = useDebounce(search, 1000);
	const { data, error, loading, refetch } = useRepresentativeRelationsData({
		contentId,
		requestToken,
		representativeUnitId: unit.id,
		sfOrgId: source.id,
		page: page - 1,
		pageSize: PAGE_SIZE,
		search: debouncedSearch,
		triggers: ['search', 'page']
	});

	useSubscribeTopicWithHandler(representativeEntityId({ unitId: unit.id, entityId: source.id, type: 'source' }), () => {
		refetch();
	});

	const peopleColumns = unit.entityFields.map(f => {
		return {
			title: f.displayName,
			dataIndex: f.customFieldId,
			render: record => {
				const customField = record.personFields?.find(field => field.customFieldId === f.customFieldId);
				const value = CustomFieldValueConverter[customField.type]?.(customField?.value, customField?.meta);
				return customField.type === 'check-box' ? (
					customField.value === '1' ? (
						<Icon iconName="Done" />
					) : (
						<Icon iconName="Cancel" />
					)
				) : (
					<Typography>{value || '-'}</Typography>
				);
			}
		};
	});
	return (
		<FlexColumn style={{ marginTop: '1rem' }}>
			<SearchTitleWrapper>
				<Typography variant="h4">{source.name}</Typography>
				<TextField
					value={search ?? ''}
					onChange={e => setSearch(e.target.value)}
					autoFocus
					sx={{ width: '200px' }}
					size="small"
					placeholder="Search"
					endAdornment={<Icon iconName="Search" />}
					inputProps={{
						style: { paddingLeft: 10, paddingRight: 15 }
					}}
				/>
			</SearchTitleWrapper>
			{error ? (
				<MuiAlert severity="error">{error}</MuiAlert>
			) : (
				<ResponsiveTable<EntityInfo>
					loading={loading}
					columns={[
						{
							title: 'Name',
							dataIndex: 'name',
							showOnMobileRow: true
						},
						...peopleColumns,
						{ title: 'Actions', isActionColumn: true }
					]}
					rowActionComponent={record => {
						return <AddToOrganisationDesktop key={`${record.id}_${source.id}`} record={record} source={source} />;
					}}
					mobileRowActionComponent={record => {
						return <AddToOrganisationMobile key={`${record.id}_${source.id}`} record={record} source={source} />;
					}}
					data={data?.data}
					paginationProps={{
						page,
						onPageChange: page => setPage(page),
						total: data?.totalResults ?? 0,
						itemsPerPage: PAGE_SIZE
					}}
				/>
			)}
		</FlexColumn>
	);
}

function ManagePerson({
	record,
	source,
	setOpen,
	isMobile
}: {
	record: EntityInfo;
	source: EntityInfo;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	isMobile: boolean;
}) {
	const { unit, getBasePosition, sendUpdates } = useRepresentativesContext();
	const { requestToken, contentId } = useContentContext();
	const { mutate: removePerson } = useRemoveOrgMembership({ requestToken });
	const { mutate: createOrgMembership } = useCreateOrgMembership({ requestToken });
	const [targetId, setTargetId] = useState<string>('');
	const [position, setPosition] = useState<string>('');

	const targets = unit.targetEntities;
	const positions = unit.positions;
	//check if the person has a relationship for each of the available positions with the target org
	const availableTargets = targets?.filter(t => {
		const targetRelations = record?.relations.filter(r => r.relationTargetId === t.id);
		if (targetRelations?.length > 0) {
			return !positions.every(p =>
				targetRelations.find(t => {
					const position = getBasePosition(t.position);
					return position === p;
				})
			);
		} else {
			return true;
		}
	});
	//once we have a target id, check available roles
	const availableRolesForTarget = targetId
		? positions.filter(p => {
				const targetRelations = record?.relations.filter(r => r.relationTargetId === targetId);
				if (targetRelations?.length > 0) {
					return !targetRelations.some(t => {
						const position = getBasePosition(t.position);
						return position === p;
					});
				} else {
					return true;
				}
		  })
		: positions;
	const memberOfTargets = record?.relations?.filter(m => targets.some(t => t.id === m.relationTargetId));
	return (
		<FlexColumnSpaced style={{ padding: '1rem' }}>
			{memberOfTargets?.length > 0 ? (
				<Typography>
					<b>{record.name}</b> is a member of the following target organisation(s):
				</Typography>
			) : (
				<Typography>
					<b>{record.name}</b> is not yet a member of a target organisation
				</Typography>
			)}

			{memberOfTargets?.map(memberOrg => {
				const position = getBasePosition(memberOrg.position);
				return (
					<div key={memberOrg.relationId} style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Typography>{memberOrg?.position}</Typography>

						{isMobile ? (
							<RemoveFromOrganisationMobile record={record} relation={memberOrg} />
						) : (
							<ConfirmPopover
								message={
									<Typography>
										Are you sure you would like to remove <b>{record.name}</b> as a <b>{position}</b> from{' '}
										<b>{memberOrg.relationTargetName}</b>?
									</Typography>
								}
								confirm={async () => {
									await removePerson({ contentId, id: Number(memberOrg.relationId) });
									sendUpdates({ relationTargetId: memberOrg.relationTargetId, relations: record.relations });
								}}
							>
								<LinkButton>Remove</LinkButton>
							</ConfirmPopover>
						)}
					</div>
				);
			})}
			{availableTargets?.length > 0 ? (
				<>
					<Typography>Please select an organisation below</Typography>
					<TextField
						size="small"
						label="Target organisation"
						value={targetId ?? ''}
						select
						onChange={e => {
							setTargetId(e.target.value);
						}}
					>
						{availableTargets?.map(target => (
							<MuiMenuItem value={target.id} key={target.id}>
								{target.name}
							</MuiMenuItem>
						))}
					</TextField>
				</>
			) : null}
			{targetId && availableRolesForTarget?.length > 0 ? (
				<>
					<Typography>
						Please select the role for <b>{record.name}</b> at this organisation
					</Typography>
					<TextField
						size="small"
						label="Position"
						value={position ?? ''}
						select
						onChange={e => {
							setPosition(e.target.value);
						}}
					>
						{availableRolesForTarget?.map(position => (
							<MuiMenuItem value={position} key={position}>
								{position}
							</MuiMenuItem>
						))}
					</TextField>
				</>
			) : null}
			{targetId && position ? (
				<Typography>
					You are about to add <b>{record.name}</b> from <b>{source.name}</b> to{' '}
					<b>{availableTargets?.find(t => t.id === targetId)?.name}</b> as <b>{position}</b>
				</Typography>
			) : null}
			<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
				<Button onClick={() => setOpen(false)} variant="tertiary">
					Cancel
				</Button>
				{targetId && position ? (
					<Button
						disabled={!position || !targetId}
						variant="primary"
						onClick={async () => {
							await createOrgMembership({
								contentId,
								organizationId: Number(targetId),
								personId: Number(record.id),
								positionName: `${position} (${source.name})`
							});
							sendUpdates({ relations: record.relations, relationTargetId: targetId });
							setTargetId('');
							setPosition('');
						}}
					>
						Add
					</Button>
				) : null}
			</FlexRowSpaced>
		</FlexColumnSpaced>
	);
}

function AddToOrganisationDesktop({ record, source }: { record: EntityInfo; source: EntityInfo }) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Manage
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="right"
				title="Add person"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<ManagePerson record={record} source={source} setOpen={setOpen} isMobile={false} />
			</ResponsiveDrawer>
		</>
	);
}

function AddToOrganisationMobile({ record, source }: { record: EntityInfo; source: EntityInfo }) {
	const [open, setOpen] = useState<boolean>(false);
	return (
		<FlexRow>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Manage
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Add person"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<ManagePerson record={record} source={source} setOpen={setOpen} isMobile={true} />
			</ResponsiveDrawer>
		</FlexRow>
	);
}

export function RemoveFromOrganisationMobile({ record, relation }: { record: EntityInfo; relation: Relation }) {
	const { requestToken, contentId } = useContentContext();
	const { getBasePosition, sendUpdates } = useRepresentativesContext();
	const { mutate: removePerson } = useRemoveOrgMembership({ requestToken });
	const [open, setOpen] = useState<boolean>(false);
	const position = getBasePosition(relation.position);
	return (
		<FlexRow>
			<LinkButton sx={{ fontSize: 'initial', lineHeight: 'initial' }} onClick={() => setOpen(true)}>
				Remove
			</LinkButton>
			<ResponsiveDrawer
				headerVariant="h5"
				placement="bottom"
				title="Remove person"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					if (open) setOpen(false);
				}}
			>
				<FlexColumnSpaced style={{ padding: '1rem' }}>
					<Typography>
						Are you sure you would like to remove <b>{record.name}</b> as a <b>{position}</b> from{' '}
						<b>{relation.relationTargetName}</b>?
					</Typography>
					<FlexRowSpaced style={{ justifyContent: 'flex-end' }}>
						<Button variant="tertiary" onClick={() => setOpen(false)}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={async () => {
								await removePerson({ id: Number(relation.relationId), contentId });
								sendUpdates({ relationTargetId: relation.relationTargetId, relations: record.relations });
								setOpen(false);
							}}
						>
							Remove
						</Button>
					</FlexRowSpaced>
				</FlexColumnSpaced>
			</ResponsiveDrawer>
		</FlexRow>
	);
}
